import {
  BarChart3,
  Bolt,
  Bookmark,
  Code2,
  Cog,
  CreditCard,
  Euro,
  FileEdit,
  FileUp,
  Gavel,
  Globe,
  KeyRound,
  LayoutGrid,
  Lock,
  Mail,
  Megaphone,
  Newspaper,
  ScrollText,
  Search,
  Settings,
  ShieldUser,
  UserCircle,
  Users,
} from 'lucide-react';
import { type MenuConfig } from './types';

export const MENU_SIDEBAR: MenuConfig = [
  {
    title: 'Dashboards',
    icon: LayoutGrid,
    children: [{ title: 'Light Sidebar', path: '/' }],
  },
  { heading: 'User' },
  {
    title: 'Settings',
    icon: Settings,
    children: [
      { title: 'User Profile', path: '/account' },
      { title: 'Billing', path: '/account/billing' },
    ],
  },
  {
    title: 'Developer',
    icon: Code2,
    permission: 'admin.view',
    children: [
      { title: 'API Docs', path: '/developer/apis' },
      { title: 'SDKs', path: '/developer/sdks' },
      { title: 'MCP Server', path: '/mcp' },
      { title: 'API Keys', path: '/account/api-keys' },
      { title: 'Subscriptions & Events', path: '/developer/subscriptions' },
      { title: 'Usage', path: '/account/usage' },
    ],
  },
  { heading: 'Apps' },
  {
    title: 'Admin',
    icon: ShieldUser,
    permission: 'admin.view',
    children: [
      { title: 'Users', path: '/admin/users' },
      { title: 'CRM', path: '/admin/crm' },
      { title: 'Subscriptions', path: '/admin/subscriptions' },
      { title: 'Stripe Events', path: '/admin/stripe-events' },
      { title: 'Tenders', path: '/vergabe' },
      { title: 'Logs', path: '/admin/logs' },
      {
        title: 'Log Statistics',
        path: '/admin/log-stats',
        permission: 'admin.view',
      },
      {
        title: 'Search Analytics',
        path: '/admin/search-analytics',
        permission: 'admin.view',
      },
      {
        title: 'Referrers',
        path: '/admin/referrers',
        permission: 'admin.view',
      },
      { title: 'Website Settings', path: '/admin/settings' },
      { title: 'Company Websites', path: '/admin/website' },
      { title: 'Company Overrides', path: '/admin/overrides' },
      { title: 'Company Files', path: '/admin/files' },
      { title: 'Private Companies', path: '/admin/privacy' },
      { title: 'Services', path: '/admin/services' },
      { title: 'Coverage', path: '/admin/coverage' },
      { title: 'API', path: '/admin/api' },
      { title: 'Funnel', path: '/admin/funnel' },
      { title: 'Credits', path: '/admin/credits' },
      { title: 'Watchlist', path: '/admin/watchlist' },
      { title: 'Emails', path: '/admin/emails' },
      { title: 'Publications', path: '/admin/publications' },
      { title: 'Pricing', path: '/plans' },
    ],
  },
];

export const ADMIN_MENU_ITEMS: MenuConfig = [
  {
    title: 'Dashboard',
    path: '/admin',
    icon: LayoutGrid,
    permission: 'admin.view',
  },
  {
    title: 'Users',
    path: '/admin/users',
    icon: Users,
    permission: 'admin.view',
  },
  {
    title: 'CRM',
    path: '/admin/crm',
    icon: UserCircle,
    permission: 'admin.view',
  },
  {
    title: 'Subscriptions',
    path: '/admin/subscriptions',
    icon: Euro,
    permission: 'admin.view',
  },
  {
    title: 'Stripe Events',
    path: '/admin/stripe-events',
    icon: Bolt,
    permission: 'admin.stripe-events',
  },
  { title: 'Tenders', path: '/vergabe', icon: Gavel },
  {
    title: 'Logs',
    path: '/admin/logs',
    icon: ScrollText,
    permission: 'admin.view',
  },
  {
    title: 'Log Statistics',
    path: '/admin/log-stats',
    icon: BarChart3,
    permission: 'admin.view',
  },
  {
    title: 'Search Analytics',
    path: '/admin/search-analytics',
    icon: BarChart3,
    permission: 'admin.view',
  },
  {
    title: 'Referrers',
    path: '/admin/referrers',
    icon: BarChart3,
    permission: 'admin.view',
  },
  {
    title: 'Website Settings',
    path: '/admin/settings',
    icon: Settings,
    permission: 'admin.view',
  },
  {
    title: 'Company Websites',
    path: '/admin/website',
    icon: Globe,
    permission: 'admin.view',
  },
  {
    title: 'Company Overrides',
    path: '/admin/overrides',
    icon: FileEdit,
    permission: 'admin.view',
  },
  {
    title: 'Company Files',
    path: '/admin/files',
    icon: FileUp,
    permission: 'admin.view',
  },
  {
    title: 'Private Companies',
    path: '/admin/privacy',
    icon: Lock,
    permission: 'admin.view',
  },
  {
    title: 'Services',
    path: '/admin/services',
    icon: Cog,
    permission: 'admin.view',
  },
  {
    title: 'Coverage',
    path: '/admin/coverage',
    icon: BarChart3,
    permission: 'admin.view',
  },
  {
    title: 'API',
    path: '/admin/api',
    icon: KeyRound,
    permission: 'admin.view',
  },
  {
    title: 'Funnel',
    path: '/admin/funnel',
    icon: BarChart3,
    permission: 'admin.view',
  },
  {
    title: 'Credits',
    path: '/admin/credits',
    icon: CreditCard,
    permission: 'admin.view',
  },
  {
    title: 'Watchlist',
    path: '/admin/watchlist',
    icon: Bookmark,
    permission: 'admin.view',
  },
  {
    title: 'Emails',
    path: '/admin/emails',
    icon: Mail,
    permission: 'admin.view',
  },
  {
    title: 'Publications',
    path: '/admin/publications',
    icon: Megaphone,
    permission: 'admin.view',
  },
];

/**
 * Header navigation — the single source of truth for the public header on both
 * desktop (`MegaMenu`) and mobile (`MegaMenuMobile`).
 *
 * Item flags:
 * - `permission` — hide entirely unless the user has the permission slug.
 * - `requiresAuth` — hide entirely when there is no session.
 * - `lockedWhenLoggedOut` — show greyed-out with a lock + tooltip when no session.
 * - `badgeSource` — renderer-supplied badge (e.g. `'watchlist-unread'` for the unread count).
 * - `analyticsKey` — identifier sent to `/api/log/header-click`.
 * - `trackingEvent` — additional `trackHeaderNavClick` event name.
 * - `isMegaPanel` — desktop renders this dropdown as the multi-column mega-grid
 *   (used by Admin to group Admin + Developer sections side-by-side).
 *
 * Admin uses a 2-level structure: Admin → Section (column) → Link.
 */
export const MENU_HEADER: MenuConfig = [
  {
    title: 'Search',
    translationKey: 'navigation.search',
    path: '/search',
    icon: Search,
    analyticsKey: 'search',
    // Full page load so clicking "Search" always lands on a clean default
    // search, even when the search page is already open with stale client
    // state (e.g. after paginating to page 2).
    hardNavigate: true,
  },
  {
    title: 'Pricing',
    translationKey: 'navigation.pricing',
    path: '/plans',
    icon: CreditCard,
    analyticsKey: 'pricing',
  },
  {
    title: 'Blog',
    translationKey: 'navigation.blog',
    path: '/blog',
    icon: Newspaper,
    analyticsKey: 'blog',
  },
  {
    title: 'Admin',
    translationKey: 'navigation.admin',
    path: '/admin',
    icon: ShieldUser,
    permission: 'admin.view',
    analyticsKey: 'admin',
    isMegaPanel: true,
    children: [
      {
        title: 'Admin Panel',
        translationKey: 'navigation.admin',
        children: ADMIN_MENU_ITEMS,
      },
    ],
  },
];
